import { Alert, AlertDescription } from '@azelis/ui/components/alert';
import { Card, CardContent } from '@azelis/ui/components/card';
import { CircleCheck, Info } from 'lucide-react';

import type { Question, TableQuestion } from '@/api/types';

import { QuestionHeader } from './question-header';
import { QuestionTable } from './question-table';

interface QuestionViewProps {
  question: Question | TableQuestion;
  isTable?: boolean;
  depth?: number;
}

/** Renders one numbered question in a Card, with an "info" Alert for instructions and a
 * "success" Alert for the answer, nested sub-questions indented below their parent, and
 * thinking/citation surfaced on hover -- mirrors the Power App's result detail screen (see
 * FRONTEND_MIGRATION.md). */
export function QuestionView({ question, isTable = false, depth = 0 }: QuestionViewProps) {
  const instructions = 'instructions' in question ? question.instructions : undefined;
  const tableContent = 'table_content' in question ? question.table_content : undefined;
  const hoverDetails = [
    question.thinking && `Thinking: ${question.thinking}`,
    question.citation && `Citation: ${question.citation}`,
  ]
    .filter(Boolean)
    .join('\n\n');

  return (
    <Card
      size="sm"
      style={{ marginLeft: depth * 20 }}>
      <QuestionHeader
        number={question.number}
        text={question.text}
        hoverDetails={hoverDetails}
      />

      <CardContent className="flex flex-col gap-3">
        {instructions && (
          <Alert variant="info">
            <Info />
            <AlertDescription>{instructions}</AlertDescription>
          </Alert>
        )}

        {isTable && tableContent && <QuestionTable content={tableContent} />}

        {question.answer && (
          <Alert variant="success">
            <CircleCheck />
            <AlertDescription>{question.answer}</AlertDescription>
          </Alert>
        )}

        {question.subquestions.length > 0 && (
          <div className="flex flex-col gap-3">
            {question.subquestions.map((sub, index) => (
              <QuestionView
                key={index}
                question={sub}
                isTable={isTable}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
